import React from 'react';

import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { TranslateFunction } from 'react-localize-redux';

import { Image } from '../../../../components/atoms/Image/Image';
import { PriceTag } from '../../../../components/atoms/PriceTag';
import { QuantityControl } from '../../../../components/atoms/QuantityControl';
import { Typography } from '../../../../components/atoms/Typography';
import { QuantityControlType } from '../../../../types/basket';
import { Currency, MenuItem } from '../../../../weborder/Api';
import { ToCurrency } from '../../../../weborder/BasketControl';
import { CrossSellListSkeleton } from './components/CrossSellListSkeleton';
import { useCrossSellList } from './hooks/useCrossSellList';

export type Props = {
  addItemToBasket: QuantityControlType['onAddItem'];
  crossSellMenuItems: MenuItem[];
  currency: Currency;
  isLoading: boolean;
  languageCode: string;
  translate: TranslateFunction;
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    borderTop: `1px solid ${theme.palette.grey[400]}`,
    paddingTop: theme.spacing(4.5),
  },
  menuItemList: {
    margin: 0,
    padding: theme.spacing(2, 0, 0),
  },
  menuItem: {
    borderTop: `1px solid ${theme.palette.grey[400]}`,
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 0),
  },
  menuItemContent: {
    display: 'flex',
  },
  menuItemImage: {
    boxSizing: 'content-box',
    minWidth: '72px',
    objectFit: 'cover',
    paddingRight: theme.spacing(1),
  },
  menuItemText: {
    paddingTop: theme.spacing(0.25),
    '& .name, & .description': {
      '-webkit-box-orient': 'vertical',
      '-webkit-line-clamp': 2,
      display: '-webkit-box',
      overflow: 'hidden',
      wordBreak: 'break-word',
    },
  },
  menuItemControls: {
    minWidth: '100px',
  },
}));

export function CrossSellList(props: Props): JSX.Element {
  const {
    addItemToBasket,
    crossSellMenuItems,
    currency,
    isLoading,
    languageCode,
    translate,
  } = props;
  const classes = useStyles();

  const { crossSellList, onHandleAddItem, showCrossSellList } =
    useCrossSellList(addItemToBasket, crossSellMenuItems, translate);

  const crossSellListStyles = clsx(classes.root, 'fd-cross-sell-list');

  if (isLoading) {
    return (
      <div className={crossSellListStyles}>
        <CrossSellListSkeleton />
      </div>
    );
  }

  if (!showCrossSellList) {
    return null;
  }

  const renderMenuItem = (menuItem: MenuItem): JSX.Element => {
    const localisedPrice = ToCurrency(
      menuItem.ActualPrice,
      currency,
      languageCode,
    );

    return (
      <li
        className={classes.menuItem}
        data-fd={`cross-sell-menu-item-${menuItem.Name}`}
        key={`cross-sell-menu-item-${menuItem.MenuItemId}`}
      >
        <div className={classes.menuItemContent}>
          {menuItem.ImageUrl && (
            <Image
              alt={menuItem.Name}
              className={classes.menuItemImage}
              dimensions={[72, 72]}
              src={menuItem.ImageUrl}
            />
          )}

          <div className={classes.menuItemText}>
            <Typography className="name">{menuItem.Name}</Typography>
            <Typography className="description" variant="body2-secondary">
              {menuItem.Description}
            </Typography>
          </div>
        </div>

        <div className={classes.menuItemControls}>
          <PriceTag
            localisedPrice={localisedPrice}
            translate={translate}
            variant="right"
          />

          <QuantityControl
            itemName={menuItem.Name}
            onAddItem={(): void => onHandleAddItem(menuItem)}
            translate={translate}
          />
        </div>
      </li>
    );
  };

  const renderMenuItems = (): JSX.Element => (
    <ul className={classes.menuItemList}>
      {crossSellList.map((menuItem) => renderMenuItem(menuItem))}
    </ul>
  );

  return (
    <div className={crossSellListStyles}>
      <Typography data-fd="cross-sell-list-title" variant="h3">
        {translate('CrossSell_peopleAlsoAdded')}
      </Typography>
      {renderMenuItems()}
    </div>
  );
}
